import React from 'react';
import './DodConditionBuilderModal.scss';
import {openModal} from '@/components/form';
import {DodConditions, DodFactSet, DodRunConfig, FactCondition, SummedSelection, TimePeriodValue, ProductFilterCondition} from "@/types/DodRun";
import {RunConfigMarket} from "@/types/ReportRun";
import {cloneDeep} from "lodash";
import {
    DodConditionEditor
} from "@/components/DodConfigEditor/builders/DodLayoutBuilder/DodLayoutConditionBuilder/DodConditionBuilder";

const baseClassName = 'dod-condition-builder-modal';


interface DodConditionBuilderModalParams {
    runConfig: DodRunConfig,
    maxDate: string,
}

interface DodConditionBuilderValue {
    conditions: FactCondition[];
    facts: DodFactSet[];
    market: string;
    markets: RunConfigMarket[];
    summedMarkets: SummedSelection<RunConfigMarket[]>[];
    timePeriod: string;
    timePeriods: TimePeriodValue[];
    summedTimePeriods: SummedSelection[];
    maxDate: string;
    conditionName?: string;
    autoConditionName?: string;
    products: ProductFilterCondition;
}

export type DodConditionBuilderModalResult = DodConditions;

export async function openDodConditionBuilderModal({runConfig, maxDate}: DodConditionBuilderModalParams): Promise<DodConditionBuilderModalResult | undefined> {
    // Support legacy FactCondition[] shape as well as the new DodConditions object shape
    const legacyConditions = Array.isArray(runConfig.conditions);
    const conditionsObj = legacyConditions
        ? { facts: runConfig.conditions as unknown as FactCondition[], market: 'each_market', timePeriod: 'each_time_period' }
        : runConfig.conditions;

    return openModal<DodConditionBuilderValue>({
        className: baseClassName,
        title: 'Data On Demand Conditions',
        initialState: {
            conditions: Array.isArray(conditionsObj?.facts) ? cloneDeep(conditionsObj.facts) : [],
            facts: runConfig.facts,
            market: conditionsObj?.market ?? 'each_market',
            markets: (runConfig.filters?.markets?.values as RunConfigMarket[]) ?? [],
            summedMarkets: (runConfig.filters?.markets?.summedSelections as SummedSelection<RunConfigMarket[]>[]) ?? [],
            timePeriod: conditionsObj?.timePeriod ?? 'each_time_period',
            timePeriods: (runConfig.filters?.timePeriods?.values as TimePeriodValue[]) ?? [],
            summedTimePeriods: (runConfig.filters?.timePeriods?.summedSelections as SummedSelection[]) ?? [],
            maxDate,
            conditionName: conditionsObj?.name,
            products: conditionsObj?.products ?? (conditionsObj as any)?.productFilter ?? ({ mode: 'All' } as ProductFilterCondition),
        },
        showCloseOption: false,
        content: ({state, setState}) => {

            function handleChange(e: ByzzerChangeEvent<FactCondition[]>): void {

                setState(state => ({
                    ...state,
                    conditions: e.value,
                    conditionName: undefined, // reset to auto-generate
                }))
            }

            function handleMarketChange(market: string): void {
                setState(state => ({
                    ...state,
                    market,
                    conditionName: undefined, // reset to auto-generate
                }))
            }

            function handleTimePeriodChange(timePeriod: string): void {
                setState(state => ({
                    ...state,
                    timePeriod,
                    conditionName: undefined, // reset to auto-generate
                }))
            }

            function handleConditionNameChange(conditionName: string): void {
                setState(state => ({
                    ...state,
                    conditionName,
                }))
            }

            function handleAutoConditionNameChange(autoConditionName: string): void {
                setState(state => ({
                    ...state,
                    autoConditionName,
                }))
            }

            function handleProductFilterChange(products: ProductFilterCondition | undefined): void {
                setState(state => ({
                    ...state,
                    products: products ?? ({ mode: 'All' } as ProductFilterCondition),
                    conditionName: undefined, // reset to auto-generate
                }))
            }

            return <>
                <DodConditionEditor
                    value={state.conditions}
                    facts={state.facts}
                    markets={state.markets}
                    summedMarkets={state.summedMarkets}
                    conditionMarket={state.market}
                    onMarketChange={handleMarketChange}
                    timePeriods={state.timePeriods}
                    summedTimePeriods={state.summedTimePeriods}
                    conditionTimePeriod={state.timePeriod}
                    onTimePeriodChange={handleTimePeriodChange}
                    maxDate={state.maxDate}
                    conditionName={state.conditionName}
                    onConditionNameChange={handleConditionNameChange}
                    onAutoConditionNameChange={handleAutoConditionNameChange}
                    productFilter={state.products}
                    onProductFilterChange={handleProductFilterChange}
                    onChange={handleChange}
                />
            </>
        },
        actions: [{
            key: 'cancel',
            type: 'negative',
            label: 'Cancel',
            action({resolve}) {
                resolve();
            }
        }, {
            key: 'clear',
            type: 'negative',
            label: 'Remove All Conditions',
            action({resolve, state}) {
                resolve({ facts: [], market: state.market, timePeriod: state.timePeriod, name: undefined, products: { mode: 'All' } });
            }
        }, {
            key: 'create',
            label: 'Apply',
            disableIf({state}) {
                const hasIncompleteCondition = Array.isArray(state.conditions) && state.conditions.some(v => {
                    const isRange = v.comparator === 'btw' || v.comparator === 'nbtw' || v.comparator === 'between';
                    if (isRange) {
                        return !v.fact || !v.comparator || !Array.isArray(v.value) || !v.value[0] || !v.value[1];
                    }
                    return !v.fact || !v.comparator || typeof v.value !== 'string' || !v.value;
                });
                if (hasIncompleteCondition) return true;
                const { products } = state;
                if (products?.mode === 'Top' || products?.mode === 'Bottom') {
                    return !products.count || !products.sortByFact;
                }
                return false;
            },
            action({resolve, state}) {
                resolve({ facts: state.conditions, market: state.market, timePeriod: state.timePeriod, name: state.conditionName || state.autoConditionName, products: state.products ?? { mode: 'All' } });
            }
        }]
    });
}
